import type { ComponentType, ReactNode } from "react";

import { Button, Icon, Paper } from "@mui/material";
import { useId, useMemo, useRef } from "react";

import { CaptionText } from "~/components/Typographies";

export interface SelectTopAction {
	disabledReason?: string;
	label: string;
	onClick: () => void;
}

export type UseTopActionPaperReturn = { paper: ComponentType<{ children?: ReactNode }> } | undefined;

const useTopActionPaper = (topAction: SelectTopAction | undefined, closeList: () => void): UseTopActionPaperReturn => {
	const reasonId = useId();
	const actionRef = useRef({ closeList, onClick: topAction?.onClick });
	actionRef.current = { closeList, onClick: topAction?.onClick };
	const label = topAction?.label;
	const disabledReason = topAction?.disabledReason;

	return useMemo(() => {
		if (!label) return undefined;
		const TopActionPaper = ({ children }: { children?: ReactNode }) => (
			<Paper>
				<Button
					aria-describedby={disabledReason ? reasonId : undefined}
					disabled={Boolean(disabledReason)}
					fullWidth
					onMouseDown={(event) => {
						event.preventDefault();
						event.stopPropagation();
						actionRef.current.closeList();
						actionRef.current.onClick?.();
					}}
					startIcon={<Icon>add_circle</Icon>}
					sx={{ justifyContent: "flex-start", minHeight: 48, px: 2 }}
					variant="text"
				>
					{label}
				</Button>
				{disabledReason && (
					<CaptionText
						color="text.secondary"
						id={reasonId}
						onMouseDown={(event) => event.preventDefault()}
						sx={{ display: "block", pb: 1, px: 2 }}
					>
						{disabledReason}
					</CaptionText>
				)}
				{children}
			</Paper>
		);
		return { paper: TopActionPaper };
	}, [disabledReason, label, reasonId]);
};

export default useTopActionPaper;
